// src/components/Fortnox/FortnoxSetupWizard.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "../../constants";
import { useAuth } from "../../navigation/AuthProvider";

export default function FortnoxSetupWizard({ onClose, exportPayload }) {
  const {
    user,
    company,
    employees = [],
    obs = [],
    overtimeRules = [],
    absenceRules = [],
    vacationPay,
  } = useAuth();

  const provider = "fortnox";

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [errorText, setErrorText] = useState("");

  const [employmentMapping, setEmploymentMapping] = useState({});
  const [search, setSearch] = useState("");

  const [paxmlMappings, setPaxmlMappings] = useState({
    defaults: {
      base: "ARB",
      vacation: "SEMERS",
    },
    obRules: {},
    overtime: {},
    absence: {},
  });

  const [mappingSummary, setMappingSummary] = useState(null);
  const [exportSummary, setExportSummary] = useState(null);

  const [showPreExportInfo, setShowPreExportInfo] = useState(false);

  const vacationItems = useMemo(() => {
    if (!vacationPay) return [];
    if (Array.isArray(vacationPay)) return vacationPay;
    return [vacationPay];
  }, [vacationPay]);

  const getTsUserId = (emp) => {
    if (!emp) return "";

    if (emp._id && typeof emp._id === "string") return emp._id;
    if (emp._id && typeof emp._id === "object") return String(emp._id);

    if (emp.user && typeof emp.user === "string") return emp.user;
    if (emp.user && typeof emp.user === "object" && emp.user._id)
      return String(emp.user._id);

    if (emp.userId) return String(emp.userId);
    if (emp.employeeId) return String(emp.employeeId);

    return "";
  };

  const getEmpDisplayName = (emp) => {
    if (!emp) return "Okänd";
    if (emp.name) return emp.name;
    if (emp.user && typeof emp.user === "object" && emp.user.name)
      return emp.user.name;
    return "Okänd";
  };

  const getEmpDisplayEmail = (emp) => {
    if (!emp) return "Ingen e-post";
    if (emp.email) return emp.email;
    if (emp.user && typeof emp.user === "object" && emp.user.email)
      return emp.user.email;
    return "Ingen e-post";
  };

  const getEmpDisplayRole = (emp) => {
    if (!emp) return "";
    if (emp.role || emp.title) return emp.role || emp.title;
    if (
      emp.user &&
      typeof emp.user === "object" &&
      (emp.user.role || emp.user.title)
    )
      return emp.user.role || emp.user.title;
    return "";
  };

  useEffect(() => {
    const run = async () => {
      if (!user?._id || !company?._id) return;

      try {
        setInitLoading(true);
        setErrorText("");

        const { data } = await axios.post(
          `${BACKEND_URL}/payroll/mappings/get`,
          {
            userId: user._id,
            companyId: company._id,
            provider,
          }
        );

        if (data?.status !== "success") {
          setErrorText(data?.errorText || "Kunde inte hämta sparade kopplingar.");
          setInitLoading(false);
          return;
        }

        const savedEmployment = data?.employeeMappings || {};
        const savedPax =
          data?.salaryMappings ||
          data?.paxmlMappings ||
          data?.salaryMapping ||
          null;

        const initialEmployment = {};
        (employees || []).forEach((emp) => {
          const tsId = getTsUserId(emp);
          if (!tsId) return;
          initialEmployment[tsId] = (savedEmployment[tsId] || "").toString();
        });
        setEmploymentMapping(initialEmployment);

        if (savedPax && typeof savedPax === "object") {
          setPaxmlMappings((prev) => {
            const next = {
              ...prev,
              ...savedPax,
              defaults: {
                ...(prev.defaults || {}),
                ...(savedPax.defaults || {}),
              },
              obRules: { ...(prev.obRules || {}), ...(savedPax.obRules || {}) },
              overtime: {
                ...(prev.overtime || {}),
                ...(savedPax.overtime || {}),
              },
              absence: {
                ...(prev.absence || {}),
                ...(savedPax.absence || {}),
              },
            };
            return next;
          });
        }

        setInitLoading(false);
      } catch (e) {
        console.error("❌ [FortnoxWizard] get-mappings error:", e);
        setErrorText("Tekniskt fel när sparade kopplingar skulle hämtas.");
        setInitLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, company?._id, provider, employees]);

  useEffect(() => {
    setPaxmlMappings((prev) => {
      const next = { ...prev };

      const ensureRuleCode = (groupKey, rulesArr) => {
        const map = { ...(next[groupKey] || {}) };
        (rulesArr || []).forEach((r) => {
          if (!r || !r._id) return;
          const id = String(r._id);
          const modelCode = (r.salaryCode || "").toString().trim();

          if (modelCode) {
            map[id] = modelCode;
            return;
          }

          if (map[id] !== undefined && map[id] !== "") return;

          map[id] = "";
        });
        next[groupKey] = map;
      };

      ensureRuleCode("obRules", obs);
      ensureRuleCode("overtime", overtimeRules);
      ensureRuleCode("absence", absenceRules);

      next.defaults = {
        base: (next.defaults?.base || "").toString().trim(),
        vacation:
          (next.defaults?.vacation || "SEMERS").toString().trim() || "SEMERS",
      };

      return next;
    });
  }, [obs, overtimeRules, absenceRules]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;

    return (employees || []).filter((emp) => {
      const name = (getEmpDisplayName(emp) || "").toLowerCase();
      const email = (getEmpDisplayEmail(emp) || "").toLowerCase();
      const role = (getEmpDisplayRole(emp) || "").toLowerCase();
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [employees, search]);

  const mappingStats = useMemo(() => {
    const total = employees.length;
    const mapped = Object.values(employmentMapping).filter(
      (v) => (v || "").toString().trim() !== ""
    ).length;
    return { total, mapped };
  }, [employees.length, employmentMapping]);

  const hasAnyMapping = mappingStats.mapped > 0;

  const duplicateWarnings = useMemo(() => {
    const reverse = {};
    (employees || []).forEach((emp) => {
      const tsId = getTsUserId(emp);
      if (!tsId) return;

      const val = (employmentMapping[tsId] || "").toString().trim();
      if (!val) return;

      if (!reverse[val]) reverse[val] = [];
      reverse[val].push(getEmpDisplayName(emp) || getEmpDisplayEmail(emp) || tsId);
    });

    return Object.entries(reverse)
      .filter(([, list]) => list.length > 1)
      .map(([anstid, list]) => ({ anstid, names: list }));
  }, [employmentMapping, employees]);

  const hasDuplicateMappings = duplicateWarnings.length > 0;

  const handleChangeEmployment = (tsUserId, value) => {
    setEmploymentMapping((prev) => ({
      ...prev,
      [tsUserId]: value,
    }));
  };

  const updatePaxmlMapping = (groupKey, id, code) => {
    setPaxmlMappings((prev) => ({
      ...prev,
      [groupKey]: {
        ...(prev[groupKey] || {}),
        [id]: (code || "").toString(),
      },
    }));
  };

  const updateDefaultCode = (key, value) => {
    setPaxmlMappings((prev) => ({
      ...prev,
      defaults: {
        ...(prev.defaults || {}),
        [key]: (value || "").toString(),
      },
    }));
  };

  const goToStep2 = () => setStep(2);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const buildEnrichedTransactions = () => {
    const rawTransactions =
      exportPayload && Array.isArray(exportPayload.transactions)
        ? exportPayload.transactions
        : [];

    const mappedTsIds = Object.entries(employmentMapping)
      .filter(([, anstid]) => (anstid || "").toString().trim() !== "")
      .map(([tsId]) => tsId.toString());

    return rawTransactions.filter((tx) => {
      const uId = tx.employeeId ? tx.employeeId.toString() : null;
      return uId && mappedTsIds.includes(uId);
    });
  };

  const handleSaveAndExportPaxml = async () => {
    if (!user?._id || !company?._id) {
      setErrorText("Saknar användare eller företag i sessionen.");
      return;
    }

    const base = (paxmlMappings.defaults?.base || "").toString().trim();
    if (!base) {
      setErrorText("Du måste ange en standardkod för arbetstid (t.ex. ARB).");
      return;
    }

    try {
      setLoading(true);
      setErrorText("");
      setMappingSummary(null);
      setExportSummary(null);

      const { data } = await axios.post(
        `${BACKEND_URL}/payroll/mappings/save`,
        {
          userId: user._id,
          companyId: company._id,
          provider,
          employeeMappings: employmentMapping,
          salaryMappings: {
            paxml: paxmlMappings,
          },
        }
      );

      if (data?.status !== "success") {
        setErrorText(data?.errorText || "Kunde inte spara kopplingar.");
        setMappingSummary(data?.summary || null);
        setLoading(false);
        return;
      }

      setMappingSummary(data.summary || { ok: true });

      const missing = data?.summary?.missingMembershipUserIds || [];
      if (Array.isArray(missing) && missing.length > 0) {
        setErrorText(
          "Vissa anställda saknar medlemskap i företaget och kunde inte sparas. " +
            "Detta händer ofta om ägaren visades utan Membership-rad."
        );
        setLoading(false);
        return;
      }

      const meta = exportPayload?.meta || {};
      const year = meta.workYear || meta.year || "";
      const month = meta.workMonth || meta.month || "";
      const filename = `timesmart_paxml_${year}-${String(month).padStart(2, "0")}.xml`;

      const res = await axios.post(
        `${BACKEND_URL}/payroll/export/paxml`,
        {
          userId: user._id,
          companyId: company._id,
          meta,
          paxmlMappings,
          transactions: exportPayload?.transactions || [],
        },
        { responseType: "blob" }
      );

      const contentType = (res.headers?.["content-type"] || "").toLowerCase();
      if (contentType.includes("application/json")) {
        const text = await res.data.text?.();
        try {
          const json = JSON.parse(text || "{}");
          setErrorText(json?.errorText || "Export misslyckades.");
        } catch (_) {
          setErrorText("Export misslyckades (okänt fel).");
        }
        setLoading(false);
        return;
      }

      downloadBlob(new Blob([res.data], { type: "application/xml" }), filename);

      const enriched = buildEnrichedTransactions();

      setExportSummary({
        status: "success",
        downloadedFile: filename,
        sentTransactions: enriched.length,
      });

      setStep(3);
      setLoading(false);
    } catch (err) {
      console.error("❌ save/export paxml error:", err);
      setErrorText(
        "Tekniskt fel när PAXml skulle skapas. Kontrollera backend-endpointen och försök igen."
      );
      setLoading(false);
    }
  };

  const handleOpenPreExportInfo = () => {
    if (loading) return;
    if (!hasAnyMapping || hasDuplicateMappings) return;
    setErrorText("");
    setShowPreExportInfo(true);
  };

  const handleConfirmPreExportInfo = async () => {
    if (loading) return;
    setShowPreExportInfo(false);
    await handleSaveAndExportPaxml();
  };

  const handleCloseRequest = () => {
    const confirmed = window.confirm(
      "Är du säker på att du vill avbryta Fortnox-exporten just nu?\n\n" +
        "Ändringar du gjort i guiden kan behöva göras om.\n" +
        "Du kan alltid öppna guiden igen från löneexportsidan."
    );
    if (!confirmed) return;
    onClose && onClose();
  };

  const handleCloseAfterSummary = () => {
    onClose && onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          width: "800px",
          maxWidth: "90vw",
          maxHeight: "90vh",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {showPreExportInfo && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.35)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 20000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "18px",
            }}
            onClick={() => {
              if (loading) return;
              setShowPreExportInfo(false);
            }}
          >
            <div
              style={{
                width: "560px",
                maxWidth: "95%",
                backgroundColor: "white",
                borderRadius: "16px",
                boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
                border: "1px solid #e5e7eb",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: "16px 18px",
                  borderBottom: "1px solid #eef2f7",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "12px",
                    backgroundColor: "#059669",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    flexShrink: 0,
                    fontSize: "16px",
                    lineHeight: 1,
                  }}
                >
                  i
                </div>

                <div style={{ flex: 1, paddingTop: "1px" }}>
                  <div
                    style={{
                      fontWeight: 800,
                      color: "#111827",
                      fontSize: "16px",
                      lineHeight: 1.15,
                    }}
                  >
                    Heads up innan export
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      marginTop: "4px",
                      lineHeight: 1.35,
                    }}
                  >
                    TimeSmart exporterar underlag (timmar och frånvaro). Vissa delar behöver
                    kompletteras i Fortnox Lön innan lönekörning.
                  </div>
                </div>

                <button
                  onClick={() => {
                    if (loading) return;
                    setShowPreExportInfo(false);
                  }}
                  style={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    color: "#111827",
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontSize: "18px",
                    lineHeight: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  disabled={loading}
                  title="Stäng"
                >
                  ×
                </button>
              </div>

              <div style={{ padding: "16px 18px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "14px",
                      backgroundColor: "#f9fafb",
                      padding: "12px 12px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        color: "#111827",
                        fontSize: "13px",
                        marginBottom: "8px",
                      }}
                    >
                      Exporteras
                    </div>
                    <div style={{ fontSize: "12.8px", color: "#374151", lineHeight: 1.5 }}>
                      • Arbetade timmar<br />
                      • OB-timmar<br />
                      • Övertid/mertid<br />
                      • Frånvaro (timmar)
                    </div>
                  </div>

                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "14px",
                      backgroundColor: "white",
                      padding: "12px 12px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        color: "#111827",
                        fontSize: "13px",
                        marginBottom: "8px",
                      }}
                    >
                      Hanteras i Fortnox
                    </div>
                    <div style={{ fontSize: "12.8px", color: "#374151", lineHeight: 1.5 }}>
                      • Lönebelopp (tim/månad)<br />
                      • Semester/ersättning<br />
                      • Karens vid sjukdom<br />
                      • Skatt & avdrag
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: "12px",
                    padding: "10px 12px",
                    borderRadius: "12px",
                    backgroundColor: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    color: "#374151",
                    fontSize: "12.8px",
                    lineHeight: 1.4,
                  }}
                >
                  Kontrollera alltid lönekörningen i Fortnox efter import innan utbetalning.
                </div>
              </div>

              <div
                style={{
                  padding: "14px 18px",
                  borderTop: "1px solid #eef2f7",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  onClick={() => {
                    if (loading) return;
                    setShowPreExportInfo(false);
                  }}
                  style={{
                    padding: "10px 14px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    backgroundColor: "white",
                    color: "#111827",
                    fontSize: "13.5px",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  disabled={loading}
                >
                  Avbryt
                </button>

                <button
                  onClick={handleConfirmPreExportInfo}
                  style={{
                    padding: "10px 14px",
                    border: "none",
                    borderRadius: "12px",
                    backgroundColor: loading ? "#d1d5db" : "#059669",
                    color: "white",
                    fontSize: "13.5px",
                    fontWeight: 800,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  disabled={loading}
                >
                  Jag förstår, fortsätt
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#059669",
            color: "white",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
            Fortnox-export (PAXml)
          </h2>
          <button
            onClick={handleCloseRequest}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "24px",
              cursor: "pointer",
              padding: "0",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            ×
          </button>
        </div>

        <div
          style={{
            display: "flex",
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#f8fafc",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: step === 1 ? "#059669" : "#cbd5e1",
              color: "white",
              fontWeight: 600,
              marginRight: "12px",
            }}
          >
            1
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 600,
                color: step === 1 ? "#1e293b" : "#64748b",
              }}
            >
              Anställningsnummer
            </div>
            <div style={{ fontSize: "14px", color: "#64748b", marginTop: "2px" }}>
              Koppla anställda till Fortnox
            </div>
          </div>

          <div style={{ width: "40px", display: "flex", justifyContent: "center" }}>
            <div style={{ width: "2px", height: "20px", backgroundColor: "#cbd5e1" }} />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: step === 2 ? "#059669" : "#cbd5e1",
              color: "white",
              fontWeight: 600,
              marginRight: "12px",
            }}
          >
            2
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 600,
                color: step === 2 ? "#1e293b" : "#64748b",
              }}
            >
              Registreringskoder
            </div>
            <div style={{ fontSize: "14px", color: "#64748b", marginTop: "2px" }}>
              Ange koder för Fortnox
            </div>
          </div>

          <div style={{ width: "40px", display: "flex", justifyContent: "center" }}>
            <div style={{ width: "2px", height: "20px", backgroundColor: "#cbd5e1" }} />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: step === 3 ? "#059669" : "#cbd5e1",
              color: "white",
              fontWeight: 600,
              marginRight: "12px",
            }}
          >
            3
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 600,
                color: step === 3 ? "#1e293b" : "#64748b",
              }}
            >
              Sammanställning
            </div>
            <div style={{ fontSize: "14px", color: "#64748b", marginTop: "2px" }}>
              Exportera PAXml-fil
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            overflow: "auto",
            padding: "24px",
          }}
        >
          {initLoading && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "3px solid #e5e7eb",
                  borderTopColor: "#059669",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p style={{ color: "#64748b" }}>Hämtar sparade Fortnox-kopplingar...</p>
            </div>
          )}

          {!initLoading && step === 1 && (
            <div>
              <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#1e293b" }}>
                Koppla anställda
              </h3>
              <p
                style={{
                  margin: "0 0 20px 0",
                  color: "#64748b",
                  fontSize: "14px",
                }}
              >
                Ange varje anställds <strong>anställningsnummer (anstnr/anstid)</strong>{" "}
                som används i Fortnox Lön. Detta sparas automatiskt så du inte behöver
                göra det igen.
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  marginBottom: "16px",
                }}
              >
                <span style={{ color: "#475569", fontSize: "14px" }}>
                  Kopplade:{" "}
                  <strong style={{ color: "#059669" }}>
                    {mappingStats.mapped} / {mappingStats.total}
                  </strong>
                </span>
                {employees.length > 5 && (
                  <input
                    type="text"
                    placeholder="Sök anställda..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      fontSize: "14px",
                      width: "250px",
                    }}
                  />
                )}
              </div>

              {duplicateWarnings.length > 0 && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#fef3c7",
                    border: "1px solid #f59e0b",
                    borderRadius: "8px",
                    marginBottom: "16px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start" }}>
                    <span style={{ marginRight: "8px", color: "#d97706" }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: "#92400e" }}>Varning:</strong> Samma
                      anställningsnummer är angivet flera gånger.
                      <ul style={{ margin: "8px 0 0 20px", padding: 0 }}>
                        {duplicateWarnings.map((d, i) => (
                          <li key={i} style={{ marginBottom: "4px", fontSize: "14px" }}>
                            Anstnr <strong>{d.anstid}</strong> används av:{" "}
                            {d.names.join(", ")}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {errorText && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#fee2e2",
                    border: "1px solid #ef4444",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    color: "#dc2626",
                    fontSize: "14px",
                  }}
                >
                  {errorText}
                </div>
              )}

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 200px",
                    backgroundColor: "#f8fafc",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                    TimeSmart-anställd
                  </div>
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                    Anställningsnummer (Fortnox)
                  </div>
                </div>

                {filteredEmployees.length === 0 && (
                  <div style={{ padding: "32px", textAlign: "center", color: "#6b7280" }}>
                    Inga anställda matchar din sökning.
                  </div>
                )}

                {filteredEmployees.map((emp, index) => {
                  const tsId = getTsUserId(emp);
                  if (!tsId) return null;

                  const name = getEmpDisplayName(emp);
                  const email = getEmpDisplayEmail(emp);
                  const role = getEmpDisplayRole(emp);

                  return (
                    <div
                      key={tsId}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 200px",
                        borderBottom:
                          index < filteredEmployees.length - 1 ? "1px solid #f3f4f6" : "none",
                        backgroundColor: "white",
                      }}
                    >
                      <div style={{ padding: "12px 16px" }}>
                        <div style={{ fontWeight: 500, color: "#111827" }}>{name}</div>
                        <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                          {email}
                          {role ? ` · ${role}` : ""}
                        </div>
                      </div>
                      <div style={{ padding: "12px 16px" }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="t.ex. 101"
                          value={employmentMapping[tsId] || ""}
                          onChange={(e) => handleChangeEmployment(tsId, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!initLoading && step === 2 && (
            <div>
              <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#1e293b" }}>
                Koppla registreringskoder
              </h3>
              <p
                style={{
                  margin: "0 0 20px 0",
                  color: "#64748b",
                  fontSize: "14px",
                }}
              >
                Här anger du Fortnox <strong>registreringskoder</strong>. Koderna du
                anger här är de som skickas med vid export och används i XML-filen.
              </p>

              {errorText && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#fee2e2",
                    border: "1px solid #ef4444",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    color: "#dc2626",
                    fontSize: "14px",
                  }}
                >
                  {errorText}
                </div>
              )}

              <div style={{ marginBottom: "24px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "16px", color: "#374151" }}>
                  Standardkoder
                </h4>
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 200px",
                      backgroundColor: "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                      Typ
                    </div>
                    <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                      Registreringskod
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 200px" }}>
                    <div style={{ padding: "12px 16px", borderBottom: "1px solid #f3f4f6" }}>
                      <div style={{ fontWeight: 500, color: "#111827" }}>Ordinarie timmar</div>
                      <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                        Exempel: ARB
                      </div>
                    </div>
                    <div style={{ padding: "12px 16px", borderBottom: "1px solid #f3f4f6" }}>
                      <input
                        type="text"
                        placeholder="t.ex. ARB"
                        value={paxmlMappings.defaults?.base || ""}
                        onChange={(e) => updateDefaultCode("base", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {obs.length > 0 && (
                <div style={{ marginBottom: "24px" }}>
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "16px", color: "#374151" }}>OB</h4>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 200px",
                        backgroundColor: "#f8fafc",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                        OB-regel
                      </div>
                      <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                        Registreringskod
                      </div>
                    </div>
                    {obs.map((rule) => (
                      <div
                        key={rule._id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 200px",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <div style={{ padding: "12px 16px" }}>
                          <div style={{ fontWeight: 500, color: "#111827" }}>{rule.name}</div>
                          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                            {rule.startTime}–{rule.endTime} · +{rule.additionalPay} kr/h
                          </div>
                        </div>
                        <div style={{ padding: "12px 16px" }}>
                          <input
                            type="text"
                            placeholder="t.ex. HLG / JOR / etc"
                            value={paxmlMappings.obRules?.[rule._id] || ""}
                            onChange={(e) => updatePaxmlMapping("obRules", rule._id, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {overtimeRules.length > 0 && (
                <div style={{ marginBottom: "24px" }}>
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "16px", color: "#374151" }}>
                    Övertid / mertid
                  </h4>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 200px",
                        backgroundColor: "#f8fafc",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                        Övertidsregel
                      </div>
                      <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                        Registreringskod
                      </div>
                    </div>
                    {overtimeRules.map((rule) => (
                      <div
                        key={rule._id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 200px",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <div style={{ padding: "12px 16px" }}>
                          <div style={{ fontWeight: 500, color: "#111827" }}>{rule.name}</div>
                          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                            {rule.percentage}% · {rule.description || ""}
                          </div>
                        </div>
                        <div style={{ padding: "12px 16px" }}>
                          <input
                            type="text"
                            placeholder="t.ex. MER"
                            value={paxmlMappings.overtime?.[rule._id] || ""}
                            onChange={(e) => updatePaxmlMapping("overtime", rule._id, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {absenceRules.length > 0 && (
                <div style={{ marginBottom: "24px" }}>
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "16px", color: "#374151" }}>
                    Frånvaro
                  </h4>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 200px",
                        backgroundColor: "#f8fafc",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                        Frånvarotyp
                      </div>
                      <div style={{ padding: "12px 16px", fontWeight: 600, color: "#374151" }}>
                        Registreringskod
                      </div>
                    </div>
                    {absenceRules.map((rule) => (
                      <div
                        key={rule._id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 200px",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <div style={{ padding: "12px 16px" }}>
                          <div style={{ fontWeight: 500, color: "#111827" }}>{rule.name}</div>
                          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                            {rule.percentage}% · {rule.description || ""}
                          </div>
                        </div>
                        <div style={{ padding: "12px 16px" }}>
                          <input
                            type="text"
                            placeholder="t.ex. SJUK / VAB / SEM"
                            value={paxmlMappings.absence?.[rule._id] || ""}
                            onChange={(e) => updatePaxmlMapping("absence", rule._id, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!initLoading && step === 3 && (
            <div>
              <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#1e293b" }}>
                Sammanställning
              </h3>

              {errorText && (
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#fee2e2",
                    border: "1px solid #ef4444",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    color: "#dc2626",
                    fontSize: "14px",
                  }}
                >
                  {errorText}
                </div>
              )}

              <div style={{ marginBottom: "24px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "16px", color: "#374151" }}>
                  Kopplingar
                </h4>
                <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>
                  Kopplade anställda:{" "}
                  <strong style={{ color: "#059669" }}>{mappingStats.mapped}</strong> av{" "}
                  <strong style={{ color: "#059669" }}>{mappingStats.total}</strong>.
                </p>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "16px", color: "#374151" }}>
                  PAXml-export
                </h4>
                {exportSummary?.status === "success" ? (
                  <>
                    <p style={{ margin: "0 0 8px 0", color: "#64748b", fontSize: "14px" }}>
                      Fil nedladdad:{" "}
                      <strong style={{ color: "#059669" }}>{exportSummary.downloadedFile}</strong>
                    </p>
                    <p style={{ margin: "0 0 8px 0", color: "#64748b", fontSize: "14px" }}>
                      Antal rader i UI-underlaget:{" "}
                      <strong style={{ color: "#059669" }}>{exportSummary.sentTransactions}</strong>
                    </p>
                    <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>
                      Nästa steg: importera XML-filen i Fortnox Lön → Kalender.
                    </p>
                  </>
                ) : (
                  <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>
                    Ingen export genomfördes.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            padding: "20px 24px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f9fafb",
          }}
        >
          <div>
            {step > 1 && step < 3 && (
              <button
                onClick={() => setStep(step - 1)}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  backgroundColor: "white",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
                disabled={loading}
              >
                Tillbaka
              </button>
            )}
          </div>

          <div>
            {step === 1 && (
              <button
                onClick={goToStep2}
                style={{
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: hasAnyMapping && !hasDuplicateMappings ? "#059669" : "#cbd5e1",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: hasAnyMapping && !hasDuplicateMappings ? "pointer" : "not-allowed",
                }}
                disabled={!hasAnyMapping || loading || hasDuplicateMappings}
              >
                {hasDuplicateMappings
                  ? "Lös dubbla anställningsnummer ovan"
                  : hasAnyMapping
                  ? "Fortsätt till registreringskoder"
                  : "Ange minst ett anställningsnummer för att fortsätta"}
              </button>
            )}

            {step === 2 && (
              <button
                onClick={handleOpenPreExportInfo}
                style={{
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor:
                    loading || !hasAnyMapping || hasDuplicateMappings ? "#cbd5e1" : "#059669",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor:
                    loading || !hasAnyMapping || hasDuplicateMappings ? "not-allowed" : "pointer",
                }}
                disabled={loading || !hasAnyMapping || hasDuplicateMappings}
              >
                {hasDuplicateMappings
                  ? "Lös dubbla anställningsnummer ovan"
                  : loading
                  ? "Sparar & skapar PAXml..."
                  : "Spara & skapa PAXml"}
              </button>
            )}

            {step === 3 && (
              <button
                onClick={handleCloseAfterSummary}
                style={{
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: "#059669",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
                disabled={loading}
              >
                Stäng
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
